/**
 * Service for interacting with the Docling Document Conversion API
 */

// Determine environment-based endpoint
const getDoclingBaseUrl = (): string => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  
  // Check if we're in production (us-east-1) or dev (us-east-2)
  if (apiBaseUrl.includes('us-east-1')) {
    // Production
    return 'https://i26p7bsn9z.us-east-1.awsapprunner.com';
  } else {
    // Development (default)
    return 'https://cstdqpumnj.us-east-2.awsapprunner.com';
  }
};

export interface DoclingConversionResult {
  filename: string;
  status: 'success' | 'error';
  data?: string;
  error?: string;
}

export interface DoclingBatchResponse {
  results: DoclingConversionResult[];
}

class DoclingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getDoclingBaseUrl();
  }

  /**
   * Convert a file to markdown format
   * @param file The file to convert (PDF, PPTX, DOCX, XLSX)
   * @returns Extracted text content as markdown
   */
  async convertFileToMarkdown(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('files[]', file);
    formData.append('format', 'markdown');

    try {
      const response = await fetch(`${this.baseUrl}/v1/convert/file`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Docling API error: ${response.status} ${response.statusText}`);
      }

      const result: DoclingBatchResponse = await response.json();
      
      if (result.results && result.results.length > 0) {
        const fileResult = result.results[0];
        
        if (fileResult.status === 'success' && fileResult.data) {
          return fileResult.data;
        } else {
          throw new Error(fileResult.error || 'Failed to convert document');
        }
      } else {
        throw new Error('No conversion result returned');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Document extraction failed: ${error.message}`);
      }
      throw new Error('Document extraction failed');
    }
  }

  /**
   * Check if a file type is supported by Docling
   * @param mimeType The MIME type of the file
   * @returns True if the file type is supported
   */
  isSupportedDocumentType(mimeType: string): boolean {
    const supportedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    return supportedTypes.includes(mimeType);
  }

  /**
   * Get file extension from filename
   * @param filename The filename
   * @returns The file extension (lowercase, without dot)
   */
  getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Check if file extension is supported (fallback when MIME type is unreliable)
   * @param filename The filename
   * @returns True if the file extension is supported
   */
  isSupportedByExtension(filename: string): boolean {
    const ext = this.getFileExtension(filename);
    const supportedExtensions = ['pdf', 'pptx', 'ppt', 'docx', 'doc', 'xlsx', 'xls'];
    return supportedExtensions.includes(ext);
  }
}

export const doclingService = new DoclingService();
export default DoclingService;

